// feature/notion-chrome — Notion-grade sidebar.
//   - Frosted glass background (--glass-bg-strong + backdrop-filter)
//   - Per-item icon + hover micro-interactions
//   - Sliding active indicator (single absolutely-positioned bar)
//   - Collapsible groups (state persists per user via localStorage)
//   - Live count badges (Devueltos / Acciones tardías / Errores) — polled
//   - Drag-to-resize right edge; auto-collapse below 120px to icon-only mode
//   - Tooltip primitive surfaces labels in collapsed mode
//   - Sidebar search trigger redirects to Cmd+K (no duplicate search)
//   - Pinned items via right-click → "Anclar arriba"; persisted via localStorage
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { users as usersApi } from "../api/endpoints";
import athenaOwl from "../assets/athena_owl.svg";
import Tooltip from "../components/Tooltip";
import { useContextMenu } from "../components/ContextMenu";
import {
  ICON_FOR_PATH, COUNT_KEY_FOR_PATH,
  IconChevron, IconSearch, IconFolder,
} from "./sidebarIcons";

const SIDEBAR_MIN = 60;
const SIDEBAR_MAX = 320;
const SIDEBAR_DEFAULT = 240;
const COLLAPSE_THRESHOLD = 120;

const NAV = {
  admin: [
    { group: "panel", label: "Panel" },
    { id: "dashboard",       to: "/dashboard",              label: "Panel" },
    { id: "urgentes",        to: "/actuaciones-urgentes",   label: "Actuaciones urgentes" },
    { group: "acciones",     label: "Acciones" },
    { id: "cases",           to: "/cases",                  label: "Casos" },
    { id: "new-case",        to: "/cases/new",              label: "Nueva acción" },
    { id: "late-case",       to: "/cases/late",             label: "Registro extemporáneo" },
    { group: "auditoria",    label: "Auditoría" },
    { id: "audit",           to: "/audit",                  label: "Auditoría" },
    { id: "auditoria-diaria",to: "/admin/auditoria-diaria", label: "Auditoría diaria" },
    { id: "contestaciones",  to: "/contestaciones",         label: "Contestaciones" },
    { group: "insumos",      label: "Insumos" },
    { id: "insumos-solicitados", to: "/insumos/solicitados", label: "Solicitados" },
    { id: "insumos-recibidos",   to: "/insumos/recibidos",   label: "Recibidos" },
    { id: "insumos-adjudicados", to: "/insumos/adjudicados", label: "Adjudicados" },
    { id: "insumos-devueltos",   to: "/insumos/devueltos",   label: "Insumos devueltos" },
    { group: "fallos",       label: "Fallos" },
    { id: "fallos-1a-fav",   to: "/fallos/primera/favorables",     label: "1ª instancia · Favorables" },
    { id: "fallos-1a-des",   to: "/fallos/primera/desfavorables",  label: "1ª instancia · Desfavorables" },
    { id: "fallos-2a-fav",   to: "/fallos/segunda/favorables",     label: "2ª instancia · Favorables" },
    { id: "fallos-2a-des",   to: "/fallos/segunda/desfavorables",  label: "2ª instancia · Desfavorables" },
    { id: "fallos-historico",to: "/fallos/historico",              label: "Histórico" },
    { group: "despacho",     label: "Despacho" },
    { id: "despacho-docs",   to: "/despacho/documentos",     label: "Documentos" },
    { group: "personas",     label: "Personas" },
    { id: "personas",        to: "/personas",               label: "Accionantes" },
    { id: "carpetas",        to: "/carpetas",               label: "Carpetas" },
    { group: "admin",        label: "Admin" },
    { id: "users",           to: "/admin/users",            label: "Usuarios" },
    { id: "audit-log",       to: "/admin/audit-log",        label: "Auditoría completa" },
    { id: "sla-breaches",    to: "/admin/sla-breaches",     label: "Vencimientos" },
    { id: "errors",          to: "/admin/error-queue",      label: "Errores" },
    { id: "late-entries",    to: "/admin/late-entry-queue", label: "Registros extemporáneos" },
    { id: "devueltos",       to: "/admin/devueltos",        label: "Devueltos" },
    { id: "moderacion-admin", to: "/moderacion",            label: "Moderación" },
    // feature/lifecycle-redesign.
    { id: "para-archivar-admin", to: "/para-archivar",      label: "Para archivar" },
    { id: "reports",         to: "/admin/reports/monthly",  label: "Reporte mensual" },
    // sprint84.1 — Informe Mensual (Contrato 225-2025) anchor on the
    // Exports page. Distinct label "Informe Previsora" to disambiguate
    // from REMANENTE's "Reporte mensual".
    { id: "informe-previsora", to: "/admin/exports#informe-mensual", label: "Informe Previsora" },
    // feature/remanentes-pendientes — anchors into the exports page panel.
    // Labeled "Remanentes del mes" (≠ the billing "Exportar REMANENTE").
    { id: "remanentes-mes", to: "/admin/exports#casos-sin-respuesta", label: "Remanentes del mes" },
    { id: "maintenance",     to: "/admin/maintenance",      label: "Mantenimiento" },
    { id: "exports",         to: "/admin/exports",          label: "Exportar a LITISOFT" },
    // feature/eva-litisoft — Eva sits in her own "Asistente" section directly
    // under "Exportar a LITISOFT" (the related LITISOFT workflow).
    { group: "eva", label: "Asistente" },
    { id: "eva-litisoft", to: "/admin/eva-litisoft", label: "Eva Litisoft" },
  ],
  reparto: [
    { group: "panel",        label: "Panel" },
    { id: "dashboard",       to: "/dashboard",              label: "Panel" },
    { group: "acciones",     label: "Acciones" },
    { id: "cases",           to: "/cases",                  label: "Casos" },
    { id: "new-case",        to: "/cases/new",              label: "Nueva acción" },
    { id: "late-case",       to: "/cases/late",             label: "Registro extemporáneo" },
    // feature/lifecycle-redesign.
    { id: "para-archivar",   to: "/para-archivar",          label: "Para archivar" },
    { group: "auditoria",    label: "Auditoría" },
    { id: "audit",           to: "/audit",                  label: "Auditoría" },
    { id: "devueltos",       to: "/admin/devueltos",        label: "Devueltos" },
    { id: "late-entries",    to: "/admin/late-entry-queue", label: "Registros extemporáneos" },
    { id: "errors",          to: "/admin/error-queue",      label: "Errores" },
    { group: "personas",     label: "Personas" },
    { id: "personas",        to: "/personas",               label: "Accionantes" },
  ],
  directora: [
    { group: "panel",        label: "Panel" },
    { id: "dashboard",       to: "/directora",              label: "Panel" },
    { group: "operaciones",  label: "Operaciones" },
    { id: "cases",           to: "/directora/cases",        label: "Casos" },
    { id: "devueltos",       to: "/directora/devueltos",    label: "Devueltos" },
    { id: "late-entries",    to: "/admin/late-entry-queue", label: "Registros extemporáneos" },
    // feature/moderation-escalation.
    { id: "moderacion",      to: "/moderacion",             label: "Moderación" },
    // feature/lifecycle-redesign.
    { id: "para-archivar-directora", to: "/para-archivar",  label: "Para archivar" },
    { group: "supervision",  label: "Supervisión" },
    { id: "audit-log",       to: "/directora/audit-log",    label: "Auditoría completa" },
    { id: "reports",         to: "/directora/reports",      label: "Reporte mensual" },
    // sprint84.1 — same Informe Previsora surface, directora variant.
    { id: "informe-previsora-directora", to: "/directora/exports#informe-mensual", label: "Informe Previsora" },
    { id: "exports",         to: "/directora/exports",      label: "LITISOFT" },
  ],
  insumos: [
    { group: "panel",        label: "Panel" },
    { id: "dashboard",       to: "/dashboard",              label: "Panel" },
    { group: "trabajo",      label: "Mi trabajo" },
    { id: "my-queue",        to: "/insumos/mine",           label: "Mi cola" },
    { id: "team-queue",      to: "/insumos",                label: "Cola del equipo" },
    { group: "insumos",      label: "Insumos" },
    { id: "insumos-solicitados", to: "/insumos/solicitados", label: "Solicitados" },
    { id: "insumos-recibidos",   to: "/insumos/recibidos",   label: "Recibidos" },
    { id: "insumos-adjudicados", to: "/insumos/adjudicados", label: "Adjudicados" },
    { id: "insumos-devueltos",   to: "/insumos/devueltos",   label: "Devueltos" },
    // feature/moderation-escalation.
    { group: "moderacion",   label: "Moderación" },
    { id: "moderacion",      to: "/moderacion",             label: "Cola de moderación" },
  ],
  revisor_fallos: [
    { group: "fallos",       label: "Fallos" },
    { id: "fallos-pending",  to: "/fallos",                 label: "Pendientes de revisar" },
    { id: "fallos-historic", to: "/fallos/historic",        label: "Histórico" },
  ],
  // feature/reparto-insumos-dispatcher.
  reparto_insumos: [
    { group: "panel",        label: "Panel" },
    { id: "reparto-insumos-dispatcher", to: "/reparto-insumos", label: "Reparto de insumos" },
  ],
  abogado: [
    { group: "panel",        label: "Panel" },
    { id: "dashboard",       to: "/abogado",                label: "Mis casos" },
  ],
  // feature/carpetas-role — minimal nav for the carpetas role. Multi-role
  // users (Rosero/Guzmán insumos+carpetas, Hernández directora+carpetas)
  // get this group merged into their primary nav by the dedup logic
  // below, so the Carpetas link surfaces without needing to add it to
  // every other role's nav.
  carpetas: [
    { group: "carpetas",     label: "Carpetas" },
    { id: "carpetas",        to: "/carpetas",               label: "Carpetas" },
    // feature/remanente-export — Sprint P3. Carpetas role gets the
    // /admin/exports page so they can pull the monthly REMANENTE
    // Excel. The LITISOFT section on that page stays gated to
    // admin/reparto via its own role check inside Exports.js.
    { id: "exports",         to: "/admin/exports",          label: "Exportar REMANENTE" },
  ],
  // feature/litisoft-role — sprint87. Minimal single-purpose nav for the
  // dedicated litisoft role (Cristian). His only workspace is the LITISOFT
  // export view (preview + inline número/radicado completion + download).
  // Scope is enforced in three places: this nav key, the route guard in
  // App.js, and the LITISOFT_ALLOW allowlist in routes/cases.js.
  litisoft: [
    { group: "litisoft",     label: "LITISOFT" },
    { id: "litisoft-export", to: "/admin/exports",          label: "Exportar a LITISOFT" },
  ],
};

// Build a {groupId → [items]} structure for ordered rendering.
function groupItems(items) {
  const groups = []; // [{ id, label, items: [] }]
  let cur = null;
  for (const it of items) {
    if (it.group) {
      cur = { id: it.group, label: it.label, items: [] };
      groups.push(cur);
    } else if (cur) {
      cur.items.push(it);
    }
  }
  return groups;
}

function readJSON(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key) || "null") ?? fallback; }
  catch { return fallback; }
}
function writeJSON(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

export default function Sidebar() {
  const { user } = useAuth();
  const location = useLocation();
  // feature/multi-role-arch — union nav entries across ALL granted roles, not
  // just the primary one. Deduplicates by item id within each group; preserves
  // the order of NAV[primaryRole] then appends extras from secondary roles.
  const navItems = useMemo(() => {
    if (!user) return [];
    const userRoles = (user.roles && user.roles.length) ? user.roles
                    : (user.role ? [user.role] : []);
    const ordered = [user.role, ...userRoles.filter((r) => r !== user.role)];
    const seenIds = new Set();
    const seenGroups = new Set();
    const out = [];
    for (const r of ordered) {
      const items = NAV[r] || [];
      for (const it of items) {
        if (it.group) {
          if (seenGroups.has(it.group)) continue;
          seenGroups.add(it.group);
          out.push(it);
        } else {
          if (seenIds.has(it.id)) continue;
          seenIds.add(it.id);
          out.push(it);
        }
      }
    }
    return out;
  }, [user]);
  const groups = useMemo(() => groupItems(navItems), [navItems]);
  const flatItems = useMemo(() => navItems.filter(i => !i.group), [navItems]);

  // Width / collapsed state.
  const [width, setWidth] = useState(() => {
    const w = Number(localStorage.getItem("eds.sidebarWidth"));
    return Number.isFinite(w) && w > 0 ? w : SIDEBAR_DEFAULT;
  });
  const isCollapsed = width < COLLAPSE_THRESHOLD;
  const effectiveWidth = isCollapsed ? SIDEBAR_MIN : width;

  // Collapsed groups (per-user, persisted).
  const [collapsedGroups, setCollapsedGroups] = useState(() => readJSON("eds.sidebarCollapsedGroups", []));
  const toggleGroup = (id) => setCollapsedGroups(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    writeJSON("eds.sidebarCollapsedGroups", next);
    return next;
  });

  // Auto-expand a group if the active route lives inside a collapsed one.
  useEffect(() => {
    const active = flatItems.find(i => location.pathname.startsWith(i.to));
    if (!active) return;
    let parentGroup = null;
    let cur = null;
    for (const it of navItems) {
      if (it.group) cur = it.group;
      else if (it === active) { parentGroup = cur; break; }
    }
    if (parentGroup && collapsedGroups.includes(parentGroup)) {
      // Auto-expand without persisting (keeps user's intent intact).
      setCollapsedGroups(prev => prev.filter(x => x !== parentGroup));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navItems]);

  // Pinned items.
  const [pinnedIds, setPinnedIds] = useState(() => readJSON("eds.sidebarPinned", []));
  const togglePin = (id) => setPinnedIds(prev => {
    const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
    writeJSON("eds.sidebarPinned", next);
    return next;
  });
  const pinnedItems = useMemo(
    () => pinnedIds.map(id => flatItems.find(i => i.id === id)).filter(Boolean),
    [pinnedIds, flatItems]
  );

  // Live count badges.
  const [counts, setCounts] = useState({});
  const prevCountsRef = useRef({});
  const [pulsing, setPulsing] = useState({}); // { key: true } briefly on increase
  const fetchCounts = useCallback(() => {
    if (!user) return;
    usersApi.sidebarCounts()
      .then(r => setCounts(r.data || {}))
      .catch(() => {});
  }, [user]);
  useEffect(() => {
    fetchCounts();
    const t = setInterval(fetchCounts, 60_000);
    function onRefresh() { fetchCounts(); }
    window.addEventListener("eds:refresh", onRefresh);
    return () => { clearInterval(t); window.removeEventListener("eds:refresh", onRefresh); };
  }, [fetchCounts]);
  useEffect(() => {
    const prev = prevCountsRef.current;
    const next = {};
    for (const k of Object.keys(counts)) {
      if (prev[k] !== undefined && counts[k] > prev[k]) next[k] = true;
    }
    prevCountsRef.current = counts;
    if (Object.keys(next).length) {
      setPulsing(p => ({ ...p, ...next }));
      setTimeout(() => setPulsing({}), 450);
    }
  }, [counts]);

  // Drag-to-resize.
  const draggingRef = useRef(false);
  const handleDragStart = (e) => {
    e.preventDefault();
    draggingRef.current = true;
    const startX = e.clientX;
    const startWidth = isCollapsed ? SIDEBAR_MIN : width;
    let last = startWidth;
    const onMove = (mv) => {
      const delta = mv.clientX - startX;
      let w = startWidth + delta;
      if (w < COLLAPSE_THRESHOLD) w = SIDEBAR_MIN;
      else if (w > SIDEBAR_MAX) w = SIDEBAR_MAX;
      last = w;
      setWidth(w);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try { localStorage.setItem("eds.sidebarWidth", String(last)); } catch {}
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Active indicator — single absolutely-positioned bar that slides between items.
  const navRef = useRef(null);
  const [indicator, setIndicator] = useState(null); // { top, height, opacity }
  useLayoutEffect(() => {
    if (!navRef.current) return;
    const active = navRef.current.querySelector(".sidebar-nav-item.is-active");
    if (!active) { setIndicator(prev => prev ? { ...prev, opacity: 0 } : null); return; }
    const navRect = navRef.current.getBoundingClientRect();
    const itemRect = active.getBoundingClientRect();
    setIndicator({
      top: itemRect.top - navRect.top + 8,
      height: itemRect.height - 16,
      opacity: 1,
    });
  }, [location.pathname, collapsedGroups, pinnedIds, width, navItems]);

  if (!user) return null;

  // Open Cmd+K from the search trigger by dispatching a fake key event.
  // CommandPalette listens at document level for ctrl/meta+K.
  function openCommandPalette() {
    const isMac = navigator.platform.toLowerCase().includes("mac");
    const ev = new KeyboardEvent("keydown", {
      key: "k", code: "KeyK",
      bubbles: true, cancelable: true,
      ctrlKey: !isMac, metaKey: isMac,
    });
    document.dispatchEvent(ev);
  }

  return (
    <aside
      className={"sidebar" + (isCollapsed ? " is-collapsed" : "")}
      style={{ width: effectiveWidth }}
    >
      {/* Brand — feature/athena-rebrand Athena rebrand. */}
      <div className="sidebar-brand">
        <img
          src={athenaOwl}
          alt="Athena"
          className="athena-owl sidebar-brand-mark"
          width={isCollapsed ? 32 : 48}
          height={isCollapsed ? 37 : 56}
        />
        {!isCollapsed && (
          <div className="sidebar-brand-text">
            <div className="sidebar-brand-name">Athena</div>
            <div className="sidebar-brand-sub">
              Plataforma de Administración y Gestión de Procesos Judiciales
            </div>
          </div>
        )}
      </div>

      {/* Search trigger → Cmd+K */}
      <Tooltip label="Buscar (⌘K)" disabled={!isCollapsed}>
        <button className="sidebar-search-trigger" onClick={openCommandPalette}>
          <IconSearch />
          {!isCollapsed && (
            <>
              <span>Buscar…</span>
              <kbd>⌘K</kbd>
            </>
          )}
        </button>
      </Tooltip>

      <div className="sidebar-nav" ref={navRef}>
        {indicator && !isCollapsed && (
          <div
            className="sidebar-active-indicator"
            style={{ top: indicator.top, height: indicator.height, opacity: indicator.opacity }}
          />
        )}

        {/* Pinned items */}
        {pinnedItems.length > 0 && (
          <Group
            id="favoritos"
            label="Favoritos"
            collapsed={collapsedGroups.includes("favoritos")}
            onToggle={() => toggleGroup("favoritos")}
            isCollapsed={isCollapsed}
          >
            {pinnedItems.map(it => (
              <Item
                key={"pin-" + it.id}
                item={it}
                pinned
                isCollapsed={isCollapsed}
                counts={counts}
                pulsing={pulsing}
                onTogglePin={togglePin}
                isPinned
              />
            ))}
          </Group>
        )}

        {/* Regular groups */}
        {groups.map(g => (
          <Group
            key={g.id}
            id={g.id}
            label={g.label}
            collapsed={collapsedGroups.includes(g.id)}
            onToggle={() => toggleGroup(g.id)}
            isCollapsed={isCollapsed}
          >
            {g.items.map(it => (
              <Item
                key={it.id}
                item={it}
                isCollapsed={isCollapsed}
                counts={counts}
                pulsing={pulsing}
                onTogglePin={togglePin}
                isPinned={pinnedIds.includes(it.id)}
              />
            ))}
          </Group>
        ))}
      </div>

      {/* Drag handle */}
      <div
        className={"sidebar-resize-handle" + (draggingRef.current ? " dragging" : "")}
        onMouseDown={handleDragStart}
        title="Arrastre para cambiar el ancho"
      />
    </aside>
  );
}

function Group({ id, label, collapsed, onToggle, isCollapsed, children }) {
  if (isCollapsed) {
    // In icon-only mode the group header is hidden; items render flat.
    return <div className="sidebar-group">{children}</div>;
  }
  return (
    <div className="sidebar-group">
      <button
        type="button"
        className="sidebar-group-header"
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <span className={"sidebar-group-chevron" + (!collapsed ? " expanded" : "")}>
          <IconChevron />
        </span>
        <span>{label}</span>
      </button>
      <div className={"sidebar-group-content" + (collapsed ? " collapsed" : "")}>
        {children}
      </div>
    </div>
  );
}

function Item({ item, isCollapsed, counts, pulsing, onTogglePin, isPinned, pinned }) {
  const Icon = ICON_FOR_PATH[item.to] || IconFolder;
  const countKey = COUNT_KEY_FOR_PATH[item.to];
  const count = countKey ? counts[countKey] || 0 : 0;
  const isPulsing = countKey ? !!pulsing[countKey] : false;
  const hasCount = countKey && count > 0;

  const { onContextMenu, menu } = useContextMenu([
    { label: isPinned ? "Desanclar" : "Anclar arriba",
      icon: "📌",
      onClick: () => onTogglePin(item.id) },
    { label: "Abrir en nueva pestaña",
      icon: "↗",
      onClick: () => window.open(item.to, "_blank") },
  ]);

  return (
    <>
      <Tooltip label={item.label} disabled={!isCollapsed}>
        <NavLink
          to={item.to}
          end={item.to === "/"}
          onContextMenu={onContextMenu}
          className={({ isActive }) =>
            "sidebar-nav-item"
            + (isActive ? " is-active" : "")
            + (hasCount ? " has-count" : "")
          }
        >
          <span className="sidebar-nav-icon"><Icon /></span>
          {!isCollapsed && (
            <>
              <span className="sidebar-nav-label">{item.label}</span>
              {pinned && <span className="sidebar-pin-mark" aria-hidden="true">📌</span>}
              {hasCount && (
                <span className={"sidebar-count-badge" + (isPulsing ? " pulsing" : "")}>
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </>
          )}
        </NavLink>
      </Tooltip>
      {menu}
    </>
  );
}
